import Navbar from '../components/Navbar';
import { colors, typography, components, presets, cn, Footer } from '../components/Constants';

export default function SLA() {
  return (
    <>
      <Navbar />
      <div className={presets.pageWrapper}>
        <div className={presets.mainCard}>
          <h1 className={cn(typography.h1, colors.text.gradient)}>Service Level Agreement</h1>
          <p className={typography.small}>Last Updated: October 27, 2025</p>
          
          <p className={typography.bodyMb}>
            This Service Level Agreement outlines the performance standards and commitments KaydSystems makes to our customers.
          </p>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>1. Service Availability</h2>
            
            <h3 className={presets.subsectionTitle}>1.1 Uptime Commitment</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>We commit to the following uptime levels based on your service tier:</p>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Standard Tier:</strong> 99.5% monthly uptime (approximately 3.6 hours downtime/month)</li>
              <li><strong className={typography.strong}>Professional Tier:</strong> 99.9% monthly uptime (approximately 43 minutes downtime/month)</li>
              <li><strong className={typography.strong}>Enterprise Tier:</strong> 99.95% monthly uptime (approximately 22 minutes downtime/month)</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>1.2 Uptime Calculation</h3>
            <p className={cn(presets.bodyText, 'mb-2')}>Uptime is calculated as:</p>
            <div className={cn(components.codeBlock, 'mb-4')}>
              <code className={typography.code}>
                Uptime % = (Total Minutes in Month - Downtime Minutes) / Total Minutes in Month × 100
              </code>
            </div>
            
            <h3 className={presets.subsectionTitle}>1.3 Excluded Downtime</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>The following are not counted as downtime:</p>
            <ul className={presets.listItem}>
              <li>Scheduled maintenance (with 48-hour notice)</li>
              <li>Emergency security patches (with reasonable notice)</li>
              <li>Issues caused by customer actions or misconfigurations</li>
              <li>Third-party service failures beyond our control</li>
              <li>Force majeure events</li>
              <li>DDoS attacks or other malicious activities</li>
              <li>Customer-requested changes or deployments</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>2. Support Response Times</h2>
            
            <h3 className={presets.subsectionTitle}>2.1 Severity Levels</h3>
            
            <div className={cn(components.alertBox('red'), 'mb-4')}>
              <p className={cn('font-bold', colors.text.primary, 'mb-2')}>Critical (P1): Complete service outage or security breach</p>
              <ul className={cn('list-disc list-inside space-y-1', colors.text.secondary, 'ml-4')}>
                <li>Standard: 2-hour response, 4-hour resolution target</li>
                <li>Professional: 1-hour response, 2-hour resolution target</li>
                <li>Enterprise: 30-minute response, 1-hour resolution target</li>
              </ul>
            </div>
            
            <div className={cn(components.alertBox('orange'), 'mb-4')}>
              <p className={cn('font-bold', colors.text.primary, 'mb-2')}>High (P2): Major functionality impaired, significant performance degradation</p>
              <ul className={cn('list-disc list-inside space-y-1', colors.text.secondary, 'ml-4')}>
                <li>Standard: 4-hour response, 8-hour resolution target</li>
                <li>Professional: 2-hour response, 4-hour resolution target</li>
                <li>Enterprise: 1-hour response, 2-hour resolution target</li>
              </ul>
            </div>
            
            <div className={cn(components.alertBox('yellow'), 'mb-4')}>
              <p className={cn('font-bold', colors.text.primary, 'mb-2')}>Medium (P3): Minor functionality issues, workaround available</p>
              <ul className={cn('list-disc list-inside space-y-1', colors.text.secondary, 'ml-4')}>
                <li>Standard: 8-hour response, 24-hour resolution target</li>
                <li>Professional: 4-hour response, 12-hour resolution target</li>
                <li>Enterprise: 2-hour response, 8-hour resolution target</li>
              </ul>
            </div>
            
            <div className={cn(components.alertBox('blue'), 'mb-4')}>
              <p className={cn('font-bold', colors.text.primary, 'mb-2')}>Low (P4): General questions, feature requests, documentation issues</p>
              <ul className={cn('list-disc list-inside space-y-1', colors.text.secondary, 'ml-4')}>
                <li>Standard: 24-hour response, best effort resolution</li>
                <li>Professional: 12-hour response, best effort resolution</li>
                <li>Enterprise: 4-hour response, best effort resolution</li>
              </ul>
            </div>
            
            <h3 className={presets.subsectionTitle}>2.2 Support Channels</h3>
            <ul className={presets.listItem}>
              <li><strong className={typography.strong}>Email:</strong> support@kaydsystems.com (all tiers)</li>
              <li><strong className={typography.strong}>Phone:</strong> 24/7 emergency line (Professional and Enterprise only)</li>
              <li><strong className={typography.strong}>Dedicated Slack Channel:</strong> Enterprise tier only</li>
              <li><strong className={typography.strong}>Ticketing Portal:</strong> All tiers</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>3. Performance Guarantees</h2>
            
            <h3 className={presets.subsectionTitle}>3.1 Infrastructure Performance</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Application Response Time:</strong> 95th percentile under 500ms (excluding external dependencies)</li>
              <li><strong className={typography.strong}>API Availability:</strong> 99.9% for control plane operations</li>
              <li><strong className={typography.strong}>Deployment Success Rate:</strong> 99% for standard deployments</li>
              <li><strong className={typography.strong}>Backup Success Rate:</strong> 99.5% for automated backups</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>3.2 Monitoring and Alerting</h3>
            <ul className={presets.listItem}>
              <li>24/7 infrastructure monitoring</li>
              <li>Automated alerts for critical issues</li>
              <li>Performance metrics updated every 60 seconds</li>
              <li>Alert response within defined SLA timeframes</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>4. Deployment and Change Management</h2>
            
            <h3 className={presets.subsectionTitle}>4.1 Deployment Windows</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Standard Deployments:</strong> Completed within 4 business hours of approval</li>
              <li><strong className={typography.strong}>Emergency Deployments:</strong> Initiated within 1 hour of approval (Professional and Enterprise)</li>
              <li><strong className={typography.strong}>Scheduled Deployments:</strong> Performed during agreed maintenance windows</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>4.2 Rollback Capability</h3>
            <ul className={presets.listItem}>
              <li>Automated rollback available for all deployments</li>
              <li>Rollback to last stable version within 15 minutes</li>
              <li>Zero-data-loss rollback guarantee for database changes</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>5. Security Commitments</h2>
            
            <h3 className={presets.subsectionTitle}>5.1 Security Response</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Critical Vulnerabilities:</strong> Patched within 24 hours of disclosure</li>
              <li><strong className={typography.strong}>High Vulnerabilities:</strong> Patched within 7 days</li>
              <li><strong className={typography.strong}>Security Incident Response:</strong> Initial response within 1 hour, full investigation within 24 hours</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>5.2 Compliance</h3>
            <ul className={presets.listItem}>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Compliance with industry standards (SOC 2, ISO 27001 in progress)</li>
              <li>Annual penetration testing with results shared upon request</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>6. Backup and Disaster Recovery</h2>
            
            <h3 className={presets.subsectionTitle}>6.1 Backup Frequency</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Database Backups:</strong> Continuous (point-in-time recovery)</li>
              <li><strong className={typography.strong}>Application Backups:</strong> Daily incremental, weekly full</li>
              <li><strong className={typography.strong}>Configuration Backups:</strong> After every change</li>
              <li><strong className={typography.strong}>Retention:</strong> 30 days standard, extended retention available</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.2 Recovery Objectives</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Recovery Time Objective (RTO):</strong> 4 hours for complete disaster recovery</li>
              <li><strong className={typography.strong}>Recovery Point Objective (RPO):</strong> 1 hour maximum data loss</li>
              <li><strong className={typography.strong}>Backup Restoration:</strong> 2 hours for database restoration</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.3 Disaster Recovery Testing</h3>
            <ul className={presets.listItem}>
              <li>Quarterly disaster recovery drills</li>
              <li>Annual full failover testing</li>
              <li>Customer participation available upon request</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>7. Communication and Reporting</h2>
            
            <h3 className={presets.subsectionTitle}>7.1 Status Updates</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Public status page available 24/7</li>
              <li>Email notifications for major incidents</li>
              <li>Real-time updates during outages</li>
              <li>Post-incident reports within 72 hours</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>7.2 Performance Reports</h3>
            <ul className={presets.listItem}>
              <li><strong className={typography.strong}>Standard:</strong> Quarterly uptime and performance reports</li>
              <li><strong className={typography.strong}>Professional:</strong> Monthly detailed performance analytics</li>
              <li><strong className={typography.strong}>Enterprise:</strong> Weekly reports with custom metrics</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>8. SLA Credits</h2>
            
            <h3 className={presets.subsectionTitle}>8.1 Credit Calculation</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>If we fail to meet our uptime commitments, you may be eligible for service credits:</p>
            <div className="overflow-x-auto">
              <table className={cn(components.table, 'mb-4')}>
                <thead className={colors.background.tableHeader}>
                  <tr>
                    <th className={components.tableHeader}>Monthly Uptime %</th>
                    <th className={components.tableHeader}>Service Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={components.tableCell}>99.0% - 99.5%</td>
                    <td className={components.tableCell}>10% of monthly fee</td>
                  </tr>
                  <tr className={colors.background.tableRow}>
                    <td className={components.tableCell}>95.0% - 99.0%</td>
                    <td className={components.tableCell}>25% of monthly fee</td>
                  </tr>
                  <tr>
                    <td className={components.tableCell}>Below 95.0%</td>
                    <td className={components.tableCell}>50% of monthly fee</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <h3 className={presets.subsectionTitle}>8.2 Credit Requests</h3>
            <ul className={presets.listItem}>
              <li>Credits must be requested within 30 days of the incident</li>
              <li>Maximum total credits per year: 50% of annual service fees</li>
              <li>Credits applied to future invoices, not cash refunds</li>
              <li>Request credits via email to: info@kaydsystems.com</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>9. Maintenance Windows</h2>
            
            <h3 className={presets.subsectionTitle}>9.1 Scheduled Maintenance</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Performed during agreed maintenance windows</li>
              <li>48-hour advance notice for standard maintenance</li>
              <li>Maximum 4 hours per month for scheduled maintenance</li>
              <li>Typically performed during off-peak hours</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>9.2 Emergency Maintenance</h3>
            <ul className={presets.listItem}>
              <li>Performed immediately for critical security patches</li>
              <li>Best effort notification provided</li>
              <li>Minimized impact whenever possible</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>10. Service Exclusions</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>This SLA does not cover:</p>
            <ul className={presets.listItem}>
              <li>Issues caused by customer code, configurations, or actions</li>
              <li>Performance problems due to inadequate resource allocation (if customer declined scaling recommendations)</li>
              <li>Third-party service failures (DNS providers, CDNs, external APIs)</li>
              <li>Network issues outside our control</li>
              <li>Beta or experimental features</li>
              <li>Services provided free of charge or in trial periods</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>11. SLA Review and Updates</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>
              We review and may update this SLA annually or as needed to reflect service improvements. We will notify you of any material changes at least 30 days in advance.
            </p>
          </section>
          
          <div className={cn(components.contactBox, 'mt-8')}>
            <h3 className={cn(typography.h4, colors.text.primary)}>Contact for SLA Issues</h3>
            <p className={presets.bodyText}>Email: sla@kaydsystems.com</p>

          </div>
        </div>
        
        <Footer />
      </div>
    </>
  );
}